// R9 — the mobile interleaved column. On a narrow screen there is no room for a
// second column and no room for a sheet that buries the retelling, so the
// original is woven INTO the retelling as a single scroll container: a run of
// retold prose, then the stretch of the original it corresponds to, then the
// next run. The weave itself is the pure `interleaveSegments` (see interleave.ts);
// this file is only its rendering.
//
// Because there is exactly one scroll container, the two sync hooks are OFF in
// this layout — there is nothing to sync a follower to. Both texts also render
// at ONE size (the retelling's), so the per-pane font sliders are hidden rather
// than silently rewriting the persisted `FontSizePrefs`.
//
// Design: an original stretch is set as a bracketed reference — a hairline left
// rule running the height of the inset, an Alegreya SC small-caps label with
// the contents-page dotted leader, quieter ink and tighter leading than the
// retold prose around it. Deliberately NOT a smaller font: the original is the
// harder text on the page, and shrinking it would punish exactly the reader who
// came for it. Collapsed it shows three lines; the whole label row is the
// accordion trigger.

import {
  Fragment,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
  type Ref,
} from "react";
import { ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@bindery/ui/components/ui/collapsible";
import type { CorpusAnchorPair } from "@bindery/shared/corpus/anchors";
import type {
  DocumentBlock,
  DocumentModel,
} from "@bindery/shared/corpus/document";
import { cn } from "@bindery/ui/lib/utils";
import { DocumentBlocks } from "./document-blocks";
import { interleaveSegments } from "./interleave";

/**
 * The subtree marker the gloss popover resolves selections against. In this
 * layout the retelling's runs share a scroll container with the original's and
 * carry `data-start` offsets into a DIFFERENT text, so `dom-offsets` must be
 * told which subtrees are the original's — this is that seam, and it matches
 * what `DocumentPane` already puts on the desktop original column.
 */
const ORIGINAL_PANE = "original";

function OriginalInset({
  model,
  blocks,
  glossEligible,
  onExpand,
}: {
  model: DocumentModel;
  blocks: readonly DocumentBlock[];
  glossEligible: boolean;
  onExpand: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Log in the handler, not an effect — this is a user event, and only
        // the opening half of it is worth an event (logging policy §9).
        if (next) onExpand();
      }}
      // The bottom padding lives on the wrapper, NOT on the clamped content:
      // `line-clamp` clips at the padding box, so padding under the content
      // would let a fourth, half-sliced line paint into it.
      // The bracketing rule and the leader are drawn in `--rule`, the
      // per-theme printed-rule ink (see index.css): `--border` all but
      // vanishes on the dark reading surface, and an alpha over
      // `muted-foreground` that reads as a hairline there is far under 3:1 in
      // the light theme.
      className="border-rule my-[1.5em] border-l pb-[0.7em] pl-[1.05em]"
    >
      <CollapsibleTrigger
        className="group focus-visible:ring-ring/60 flex w-full items-center gap-2 py-[0.7em] text-left focus-visible:ring-2 focus-visible:outline-none"
        aria-label={open ? "Hide the original" : "Show the original"}
      >
        <span className="font-smcp text-muted-foreground shrink-0 text-[0.79em] tracking-[0.234em] lowercase">
          Original
        </span>
        {/* Dotted leader, as on the library's printed contents page. */}
        <span
          aria-hidden="true"
          className="border-rule h-0 flex-1 border-b border-dotted"
        />
        <ChevronDown
          aria-hidden="true"
          className="text-muted-foreground size-[0.95em] shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
        />
      </CollapsibleTrigger>
      {/* `forceMount` keeps the stretch in the DOM while collapsed so the three
          clamped lines are the real text — the same nodes the gloss popover
          resolves offsets against — rather than a duplicate preview copy that
          would give `offsetToPoint` two candidates for one offset. */}
      <CollapsibleContent
        forceMount
        data-pane={ORIGINAL_PANE}
        // A keyboard gloss entry point only while the stretch is OPEN and
        // gloss-eligible: collapsed, the trigger above is already the
        // keyboard path in (Tab reaches it, Enter expands), so a second entry
        // point on the clamped preview would be redundant and would announce
        // a region full of the three visible lines, not the whole passage.
        tabIndex={open && glossEligible ? 0 : undefined}
        role={open && glossEligible ? "region" : undefined}
        aria-label={open && glossEligible ? "Original passage" : undefined}
        aria-describedby={open && glossEligible ? "gloss-kbd-hint" : undefined}
        data-gloss-entry={open && glossEligible ? "true" : undefined}
        // The original's own chapter title is a heading, not display type, once
        // it is quoted inside the retelling — it must not out-shout the retold
        // headline it sits under.
        className={cn(
          "leading-[1.55] text-foreground/70 [&>*:last-child]:mb-0 [&_h1]:mb-[0.5em] [&_h1]:text-[1.1em] data-[state=closed]:line-clamp-3",
          open &&
            glossEligible &&
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/60",
        )}
      >
        <DocumentBlocks model={model} blocks={blocks} pane="original" />
      </CollapsibleContent>
    </Collapsible>
  );
}

interface InterleavedColumnProps {
  retelling: DocumentModel;
  original: DocumentModel;
  anchors: readonly CorpusAnchorPair[];
  /** Base font size (px). One size for BOTH texts in this layout. */
  fontSize: number;
  /** Non-English source — marks the original subtrees for the gloss popover. */
  glossEligible: boolean;
  /** Called when a reader opens a stretch (`reading.original_expand`). */
  onExpandOriginal: () => void;
  /** The single scroll container — reading-nav, copy and gloss all read it. */
  ref?: Ref<HTMLDivElement>;
  className?: string;
  footer?: ReactNode;
}

export function InterleavedColumn({
  retelling,
  original,
  anchors,
  fontSize,
  glossEligible,
  onExpandOriginal,
  ref,
  className,
  footer,
}: InterleavedColumnProps) {
  // Memoized rather than recomputed every render: the reader's models are
  // promise-cache-stable, so it never noticed, but the admin workbench's
  // mobile-weave preview re-renders on every keystroke of a draft anchor edit
  // (`use-anchor-draft.ts`), and `interleaveSegments` walks every block and
  // anchor pair. Keyed on the three inputs' own identities — honest by
  // construction: a genuinely new `anchors` array (a draft edit) recomputes, an
  // unrelated re-render with the same array reference does not.
  const segments = useMemo(
    () => interleaveSegments(retelling, original, anchors),
    [retelling, original, anchors],
  );

  return (
    <div
      ref={ref}
      data-pane="retelling"
      className={cn("h-full overflow-y-auto overscroll-contain", className)}
      style={{ fontSize: `${String(fontSize)}px` } satisfies CSSProperties}
    >
      <article className="text-foreground mx-auto flex max-w-[42rem] flex-col px-6 pt-10 pb-[60vh] font-serif leading-[1.7]">
        {segments.map((segment) => (
          // Keyed by the original span's start: cuts strictly ascend, so it is
          // unique per segment and stable across re-renders. A Fragment, not a
          // wrapper element — the blocks must stay direct flex children of the
          // article so their margins never collapse (see DocumentPane).
          <Fragment key={`s-${String(segment.original.start)}`}>
            <DocumentBlocks
              model={retelling}
              blocks={segment.retellingBlocks}
              pane="retelling"
            />
            {segment.originalBlocks.length > 0 && (
              <OriginalInset
                model={original}
                blocks={segment.originalBlocks}
                glossEligible={glossEligible}
                onExpand={onExpandOriginal}
              />
            )}
          </Fragment>
        ))}
        {footer}
      </article>
    </div>
  );
}
